/**
 * design.md §4 — the `attachment` field: files a record carries, kept in Blob and
 * read through the API (routers/files.py), never from a public address.
 */

import { Download, Paperclip, Upload, X } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { api, ApiError } from '@/lib/api'
import type { FileRef } from '@/lib/meta'
import { cn } from '@/lib/utils'

export function filesOf(value: unknown): FileRef[] {
  return Array.isArray(value)
    ? value.filter((f): f is FileRef => Boolean(f && typeof f === 'object' && 'id' in f))
    : []
}

function size(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function FileLink({ file }: { file: FileRef }) {
  return (
    <a
      href={`/api/files/${file.id}`}
      target="_blank"
      rel="noreferrer noopener"
      onClick={(event) => event.stopPropagation()}
      className="inline-flex min-w-0 items-center gap-1 text-text underline decoration-border underline-offset-2 hover:text-accent hover:decoration-accent"
      title={`${file.name} · ${size(file.size)}`}
    >
      <Paperclip className="size-3.5 shrink-0 text-faint" aria-hidden />
      <span className="truncate">{file.name}</span>
    </a>
  )
}

/** In a list: the first file, and how many more. On a record's page (`full`): every file, each downloadable. */
export function AttachmentCell({ value, full = false }: { value: unknown; full?: boolean }) {
  const files = filesOf(value)
  if (files.length === 0) return <span className="text-faint">—</span>
  if (full) {
    return (
      <ul className="space-y-1">
        {files.map((file) => (
          <li key={file.id} className="flex min-w-0 items-center justify-between gap-2">
            <FileLink file={file} />
            <span className="flex shrink-0 items-center gap-2 text-[11px] text-faint">
              {size(file.size)}
              <a
                href={`/api/files/${file.id}`}
                download={file.name}
                onClick={(event) => event.stopPropagation()}
                className="inline-flex items-center gap-1 rounded p-0.5 hover:bg-surface-2 hover:text-text"
                aria-label={`Download ${file.name}`}
                title="Download"
              >
                <Download className="size-3.5" />
              </a>
            </span>
          </li>
        ))}
      </ul>
    )
  }
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <FileLink file={files[0]} />
      {files.length > 1 ? <span className="text-xs text-faint">+{files.length - 1}</span> : null}
    </span>
  )
}

/** In a form: the files so far, each removable, and Add file. */
export function AttachmentInput({
  value,
  onChange,
  disabled,
  id,
  multiple = true,
}: {
  value: unknown
  onChange: (value: FileRef[] | null) => void
  disabled?: boolean
  id?: string
  multiple?: boolean
}) {
  const files = filesOf(value)
  const input = React.useRef<HTMLInputElement>(null)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  async function add(list: FileList | null) {
    if (!list || list.length === 0) return
    setBusy(true)
    setError(null)
    try {
      const added: FileRef[] = []
      for (const file of Array.from(list)) added.push(await api.upload<FileRef>(file))
      onChange(multiple ? [...files, ...added] : added.slice(0, 1))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The file could not be added.')
    } finally {
      setBusy(false)
      if (input.current) input.current.value = ''
    }
  }

  return (
    <div className="space-y-1.5">
      {files.length > 0 ? (
        <ul className="space-y-1">
          {files.map((file) => (
            <li
              key={file.id}
              className="flex items-center justify-between gap-2 rounded-md border border-border-soft px-2 py-1 text-[13px]"
            >
              <FileLink file={file} />
              <span className="flex shrink-0 items-center gap-1 text-[11px] text-faint">
                {size(file.size)}
                {disabled ? null : (
                  <button
                    type="button"
                    onClick={() => {
                      const rest = files.filter((f) => f.id !== file.id)
                      onChange(rest.length ? rest : null)
                    }}
                    className="rounded p-0.5 hover:bg-surface-2 hover:text-text"
                    aria-label={`Remove ${file.name}`}
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {disabled || (!multiple && files.length > 0) ? null : (
        <>
          <input
            ref={input}
            id={id}
            type="file"
            multiple={multiple}
            className="sr-only"
            onChange={(event) => void add(event.target.files)}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={busy}
            onClick={() => input.current?.click()}
          >
            <Upload />
            Add file
          </Button>
        </>
      )}
      {error ? <p className={cn('text-[11px] text-danger')}>{error}</p> : null}
    </div>
  )
}
